import { isNil } from 'lodash';

// Constants.
const INCHES_IN_FOOT = 12;
const INCHES_IN_YARD = INCHES_IN_FOOT * 3;
const INCHES_IN_MILE = INCHES_IN_YARD * 1760;

const FEET_IN_YARD = 3;
const FEET_IN_MILE = 5280;
const SQUARE_FEET_IN_SQUARE_YARD = FEET_IN_YARD * FEET_IN_YARD; // 3 × 3 = 9
const SQUARE_FEET_IN_SQUARE_MILE = FEET_IN_MILE * FEET_IN_MILE; // 5280 × 5280

const METERS_IN_KILOMETER = 1000;
const METERS_IN_METER = 1;
const METERS_IN_CENTIMETER = 0.01;

const SQUARE_METERS_IN_SQUARE_KILOMETER =
  METERS_IN_KILOMETER * METERS_IN_KILOMETER; // 1000 * 1000
const SQUARE_METERS_IN_SQUARE_METER = METERS_IN_METER * METERS_IN_METER; // 1 * 1

// Enums.
export enum UnitSystem {
  Metric = 'Metric',
  Imperial = 'Imperial',
}

export enum DistanceUnitLabel {
  Millimeters = 'Millimeters',
  Centimeters = 'Centimeters',
  Meters = 'Meters',
  Kilometers = 'Kilometers',
  Inches = 'Inches',
  Feet = 'Feet',
  Yards = 'Yards',
  Miles = 'Miles',
}

export enum DistanceUnit {
  Millimeter = 'mm',
  Centimeter = 'cm',
  Meter = 'm',
  Kilometer = 'km',
  Inch = 'in',
  Foot = 'ft',
  Yard = 'yd',
  Mile = 'mi',
}

export enum AreaUnitLabel {
  SquareCentimeters = 'Square Centimeters',
  SquareMeters = 'Square Meters',
  SquareKilometers = 'Square Kilometers',
  Hectares = 'Hectares',
  Acres = 'Acres',
  SquareFeet = 'Square Feet',
  SquareYards = 'Square Yards',
  SquareMiles = 'Square Miles',
}

export enum AreaUnit {
  SquareCentimeter = 'cm²',
  SquareMeter = 'm²',
  SquareKilometer = 'km²',
  Hectare = 'ha',
  Acre = 'ac',
  SquareFoot = 'ft²',
  SquareYard = 'yd²',
  SquareMile = 'mi²',
}

export const metricMeasurementUnits: string[] = [
  DistanceUnitLabel.Millimeters,
  DistanceUnitLabel.Centimeters,
  DistanceUnitLabel.Meters,
  DistanceUnitLabel.Kilometers,
  AreaUnitLabel.SquareCentimeters,
  AreaUnitLabel.SquareMeters,
  AreaUnitLabel.SquareKilometers,
  AreaUnitLabel.Hectares,
];

export const imperialMeasurementUnits: string[] = [
  DistanceUnitLabel.Inches,
  DistanceUnitLabel.Feet,
  DistanceUnitLabel.Yards,
  DistanceUnitLabel.Miles,
  AreaUnitLabel.Acres,
  AreaUnitLabel.SquareFeet,
  AreaUnitLabel.SquareYards,
  AreaUnitLabel.SquareMiles,
];

// Conversion Factors.
const distanceFactors: Record<string, number> = {
  mm: 1000,
  cm: 100,
  m: 1,
  km: 0.001,
  in: 39.3701,
  ft: 3.28084,
  yd: 1.09361,
  mi: 0.000621371,
};

const areaFactors: Record<string, number> = {
  'cm²': 10_000, // 1 m² = 10,000 cm²
  'm²': 1,
  'km²': 0.000001,
  'ft²': 10.7639,
  ac: 0.000247105,
  ha: 0.0001,
  'yd²': 1.19599,
  'mi²': 0.000000386102,
};

/**
 * Converts meters to the given distance unit.
 * @returns a number rounded to `decimals`.
 */
export function convertFromMeters(
  meters: number,
  toUnit: DistanceUnit | string,
  decimals = 3,
): number {
  const factor = distanceFactors[toUnit];

  return parseFloat((meters * factor).toFixed(decimals));
}

/**
 * Converts square meters to the given area unit.
 * @returns a number rounded to `decimals = 3`.
 */
export function convertFromSquareMeters(
  sqMeters: number,
  toUnit: AreaUnit | string,
  decimals = 3,
): number {
  const factor = areaFactors[toUnit];

  return parseFloat((sqMeters * factor).toFixed(decimals));
}

export const getDistanceFormattedUnitValue = (
  value: number | null | undefined,
  measurementSystem = UnitSystem.Metric,
): DistanceUnitLabel | undefined => {
  if (isNil(value)) {
    return;
  }

  if (measurementSystem === UnitSystem.Imperial) {
    const valueInInches = convertFromMeters(value, DistanceUnit.Inch);

    if (valueInInches >= INCHES_IN_MILE) {
      return DistanceUnitLabel.Miles;
    }
    if (valueInInches >= INCHES_IN_YARD) {
      return DistanceUnitLabel.Yards;
    }
    if (valueInInches >= INCHES_IN_FOOT) {
      return DistanceUnitLabel.Feet;
    }
    return DistanceUnitLabel.Inches;
  }

  if (value >= METERS_IN_KILOMETER) {
    return DistanceUnitLabel.Kilometers;
  }
  if (value >= METERS_IN_METER) {
    return DistanceUnitLabel.Meters;
  }
  if (value >= METERS_IN_CENTIMETER) {
    return DistanceUnitLabel.Centimeters;
  }
  return DistanceUnitLabel.Millimeters;
};

export const getAreaFormattedUnitValue = (
  value: number | null | undefined,
  measurementSystem = UnitSystem.Metric,
): AreaUnitLabel | undefined => {
  if (isNil(value)) {
    return;
  }

  if (measurementSystem === UnitSystem.Imperial) {
    const valueInSquareFeet = convertFromSquareMeters(
      value,
      AreaUnit.SquareFoot,
    );

    if (valueInSquareFeet >= SQUARE_FEET_IN_SQUARE_MILE) {
      return AreaUnitLabel.SquareMiles;
    }
    if (valueInSquareFeet >= SQUARE_FEET_IN_SQUARE_YARD) {
      return AreaUnitLabel.SquareYards;
    }
    return AreaUnitLabel.SquareFeet;
  }

  if (value >= SQUARE_METERS_IN_SQUARE_KILOMETER) {
    return AreaUnitLabel.SquareKilometers;
  }
  if (value >= SQUARE_METERS_IN_SQUARE_METER) {
    return AreaUnitLabel.SquareMeters;
  }
  return AreaUnitLabel.SquareCentimeters;
};
